import React, { useState } from "react";
import { Modal, ConfigProvider, Button } from "antd";
import { useAppData } from "./context/DataContext";
import MessageBlastModal from "./MessageBlastModal";
import axios from "axios";
import secrets from "./secrets";



// Styles
const styles = {
  modal: {
    top: 20,
  },
  modalBody: {
    maxHeight: "calc(100vh - 225px)",
    overflow: "auto",
  },

  attendeeHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  attendeeAvatar: {
    flexShrink: 0,
  },
  nameEmailDiv: {
    flexGrow: 1,
  },
};

const api = secrets.server_url;

const AttendeesModal = ({
  isVisible,
  onCancel,
  info,
  dateIndex,
  getAttendeeDisplay,
  approvedAttendees,
  onApprovalChange,
  registeredIndex,
  handleRegistrationApproval,
  setApprovedAttendees,
  isStripe,
}) => {
  // State
  const { users, selectedOrganizationInfo, updateEventInContext } = useAppData();
  const [isMessageModalVisible, setIsMessageModalVisible] = useState(false);
  const [wasAttendeesModalVisible, setWasAttendeesModalVisible] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Helper Functions
  const getNameFromEmail = (inputEmail) => {
    const userIndex = users.findIndex((user) => user.email === inputEmail);
    return userIndex !== -1 ? users[userIndex].username : inputEmail;
  };

  const toggleApproval = (email) => {
    const isCurrentlyApproved = approvedAttendees.includes(email);

    // If removing approval, no confirmation needed
    if (isCurrentlyApproved) {
      if (onApprovalChange) {
        const newApprovedList = approvedAttendees.filter((e) => e !== email);
        onApprovalChange(newApprovedList);
      }
      return;
    }

    // If it's a paid event, show confirmation dialog
    if (isStripe) {
      Modal.confirm({
        title: "Confirm Attendee Approval",
        content:
          "This is a paid event. Are you sure you want to approve this attendee? Please confirm they have completed payment.",
        okText: "Yes, Approve",
        cancelText: "Cancel",
        onOk: () => {
          handleRegistrationApproval(email);
          if (onApprovalChange) {
            const newApprovedList = [...approvedAttendees, email];
            onApprovalChange(newApprovedList);
          }
        },
      });
    } else {
      // For free events, approve directly
      handleRegistrationApproval(email);
      if (onApprovalChange) {
        const newApprovedList = [...approvedAttendees, email];
        onApprovalChange(newApprovedList);
      }
    }
  };

  const approveRegistration = async (email) => {
    setIsLoading(true);
    if (approvedAttendees.includes(email)) {
      console.log("Registration already approved.");
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${api}approveRegistration`, {
        eventId: info.event_id,
        userEmail: email,
      });

      if (response?.data?.status) {
        setApprovedAttendees((prev) => [...prev, email]);
        updateEventInContext(response?.data?.data);
        console.log("Registration approved successfully.");
      } else {
        console.error("Failed to approve registration.");
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Error approving registration:", error);
      setIsLoading(false);
    }
  };

  const rejectRegistration = async (email) => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${api}rejectRegistration`, {
        eventId: info.event_id,
        userEmail: email,
      });

      if (response?.data?.status) {
        /*
         *   Here we need to remove the user from the registered array and update it in the data context
         */
        updateEventInContext(response?.data?.data);
        console.log("Registration rejected successfully.");
      } else {
        console.error("Failed to reject registration.");
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Error reject registration:", error);
      setIsLoading(false);
    }
  };

  // Modal Handlers
  const showMessageModal = () => {
    setWasAttendeesModalVisible(true);
    onCancel();
    setIsMessageModalVisible(true);
  };

  const handleMessageModalCancel = () => {
    setIsMessageModalVisible(false);
    if (wasAttendeesModalVisible) {
      setWasAttendeesModalVisible(false);
      setTimeout(() => onCancel(false), 100);
    }
  };

  // Render Helper Functions
  const renderAttendeeList = () => {
    if (
      !info?.registered?.[registeredIndex] ||
      info.registered[registeredIndex].length === 0
    ) {
      return (
        <div className="no-attendees">
          <p className="no-attendees-text">No attendees registered yet</p>
        </div>
      );
    }

    console.log(info)

    return info.registered[registeredIndex].map((email, index) => {
      const isApproved = info.approvedAttendees?.[registeredIndex]?.includes(email) || false;
      const formResponse = info.customFormResponses?.[email];

      return (
        <div key={index} className="attendee-card">
          <div className="attendee-header" style={styles.attendeeHeader}>
            <div className="attendee-avatar" style={styles.attendeeAvatar}>
              <span className="avatar-text">
                {getNameFromEmail(email)?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="name-email-div" style={styles.nameEmailDiv}>
              <span>
                <b>{getNameFromEmail(email)}</b>
              </span>
              <span>{email}</span>
            </div>
          </div>
          {formResponse && (
            <div className="form-response-container">
              <b>Form Responses:</b>
              <ul className="form-response-list">
                {Object.entries(formResponse).map(
                  ([question, response], idx) => (
                    <li key={idx}>
                      <b>Q{idx + 1}:</b> {response}
                    </li>
                  )
                )}
              </ul>
            </div>
          )}
          {info.requireApproval && (
            <div className="approval-buttons">
              <div
                className={`status-badge ${
                  isApproved ? "approved" : "approve"
                }`}
                onClick={() => !isApproved && approveRegistration(email)}
              >
                {isApproved ? "Approved" : "Approve"}
              </div>
              {!isApproved && (
                <div
                  className={`status-badge reject`}
                  onClick={() => rejectRegistration(email)}
                >
                  Reject
                </div>
              )}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <>
      <Modal
        className="attendees-modal"
        title={`Attendees List (${getAttendeeDisplay(info, dateIndex)})`}
        open={isVisible}
        onCancel={onCancel}
        width={800}
        style={{
          maxWidth: "90vw", // Ensures responsiveness
        }}
        bodyStyle={{
          maxHeight: "calc(100vh - 200px)",
          overflow: "auto",
          padding: "16px",
        }}
        footer={
          <div
            style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}
          >
            <Button
              
              onClick={showMessageModal}
              style={styles.messageButton}
            >
              Message Blast
            </Button>
          </div>
        }
        closable={true}
        maskClosable={true}
        centered
      >
        <div className="attendees-container">
          {" "}
          {isLoading && (
            <div className="loader-container">
              <div className="loader" id="loader-1"></div>
            </div>
          )}
          {renderAttendeeList()}
        </div>
      </Modal>

      <MessageBlastModal
        isVisible={isMessageModalVisible}
        onCancel={handleMessageModalCancel}
        info={info}
        registeredIndex={registeredIndex}
        approvedAttendees={approvedAttendees}
        selectedOrganizationInfo={selectedOrganizationInfo}
      />
</>
  );
};

export default AttendeesModal;
